
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import TimeSlotItem from "./time-selection/TimeSlotItem";
import { toast } from "@/hooks/use-toast";
import { TimeSlot } from "@/types/subscription";

interface TimeSlotManagerProps {
  timeSlots: TimeSlot[];
  setTimeSlots: (timeSlots: TimeSlot[]) => void;
  selectedCategories: string[];
}

const DEFAULT_DAYS = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: true,
  sunday: true,
};

export default function TimeSlotManager({
  timeSlots,
  setTimeSlots,
  selectedCategories
}: TimeSlotManagerProps) {
  useEffect(() => {
    if (timeSlots.length === 0) {
      const now = new Date();
      const minutes = Math.ceil(now.getMinutes() / 5) * 5;
      now.setMinutes(minutes);
      now.setSeconds(0);
      now.setMilliseconds(0);
      const defaultTime = now.toTimeString().substring(0, 5);
      setTimeSlots([{
        time: defaultTime,
        categoryId: null,
        days: DEFAULT_DAYS
      }]);
    }
  }, []);

  const addTimeSlot = () => {
    if (timeSlots.length < 4) {
      let defaultHour = 12;
      let defaultMinute = 0;
      
      if (timeSlots.length > 0) {
        const lastTime = timeSlots[timeSlots.length - 1].time;
        const [lastHour, lastMinute] = lastTime.split(':').map(Number);
        
        defaultHour = lastHour + 1;
        if (defaultHour > 23) defaultHour = 8;
        defaultMinute = lastMinute;
      }
      
      const newTime = `${defaultHour.toString().padStart(2, '0')}:${defaultMinute.toString().padStart(2, '0')}`;
      
      setTimeSlots([...timeSlots, {
        time: newTime,
        categoryId: null,
        days: DEFAULT_DAYS
      }]);
    } else {
      toast({
        title: "Maximum slots reached",
        description: "You can only have up to 4 delivery times.",
        variant: "destructive"
      });
    }
  };

  const validateTimeSlots = () => {
    const hasInvalidSlots = timeSlots.some(slot => !slot.categoryId && slot.categoryId !== 'random');
    if (hasInvalidSlots) {
      toast({
        title: "Missing category selection",
        description: "Please select a category or 'Random Category' for each time slot.",
        variant: "destructive"
      });
      return false;
    }
    return true;
  };

  const removeTimeSlot = (index: number) => {
    const newTimeSlots = [...timeSlots];
    newTimeSlots.splice(index, 1);
    setTimeSlots(newTimeSlots);
  };

  const updateTimeSlot = (index: number, field: keyof TimeSlot, value: any) => {
    const newTimeSlots = [...timeSlots];
    newTimeSlots[index] = {
      ...newTimeSlots[index],
      [field]: value
    };
    setTimeSlots(newTimeSlots);
  };

  const updateDays = (index: number, day: string, checked: boolean) => {
    const newTimeSlots = [...timeSlots];
    newTimeSlots[index] = {
      ...newTimeSlots[index],
      days: {
        ...(newTimeSlots[index].days || DEFAULT_DAYS),
        [day]: checked
      }
    };
    setTimeSlots(newTimeSlots);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-center">Select Delivery Times</h3>
      <p className="text-center text-gray-600 text-sm mb-4">Choose when you'd like to receive quotes and from which category.</p>
      
      <div className="space-y-6">
        {timeSlots.map((slot, index) => (
          <TimeSlotItem 
            key={index} 
            time={slot.time} 
            categoryId={slot.categoryId}
            days={slot.days}
            selectedCategories={selectedCategories}
            showRandomOption={true}
            onTimeChange={(hours, minutes) => {
              updateTimeSlot(index, 'time', `${hours}:${minutes}`);
            }}
            onCategoryChange={categoryId => {
              updateTimeSlot(index, 'categoryId', categoryId);
            }}
            onDaysChange={(day, checked) => {
              updateDays(index, day, checked);
            }}
            onRemove={() => removeTimeSlot(index)}
          />
        ))}
      </div>
      
      {timeSlots.length < 4 && (
        <Button 
          type="button" 
          variant="outline" 
          className="w-full mt-2" 
          onClick={addTimeSlot}
        >
          <Plus size={16} className="mr-2" />
          Add Another Time
        </Button>
      )}
    </div>
  );
}
